import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Award,
  LogOut,
  Settings,
  Shield,
  Target,
  User as UserIcon,
  UserCircle,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { ROUTES } from '@/app/routes';
import {
  Avatar,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui';

interface UserMenuProps {
  /**
   * 'sidebar' is the desktop row with name and handle; 'compact' is the avatar
   * button in the mobile top bar. Compact also carries the destinations that
   * don't fit the five-slot BottomNav — without it Settings, Communities,
   * Challenges and Sign out have no entry point at all on a phone.
   */
  variant?: 'sidebar' | 'compact';
}

export function UserMenu({ variant = 'sidebar' }: UserMenuProps) {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  if (!user) return null;

  const compact = variant === 'compact';
  const name = user.displayName ?? user.username;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className={
          compact
            ? 'grid h-9 w-9 shrink-0 place-items-center rounded-full transition-colors hover:bg-parchment-dark/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent coarse:h-11 coarse:w-11'
            : 'flex w-full items-center gap-3 rounded-md p-2 text-left hover:bg-parchment-dark/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent'
        }
        aria-label={t('nav.menu')}
      >
        <Avatar src={user.avatarUrl} name={name} size="sm" />
        {!compact && (
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-parchment-ink">{name}</p>
            <p className="truncate text-xs text-parchment-muted">@{user.username}</p>
          </div>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" side={compact ? 'bottom' : 'top'} className="w-56">
        {compact && (
          <>
            <div className="px-2 py-1.5">
              <p className="truncate text-sm font-medium text-parchment-ink">{name}</p>
              <p className="truncate text-xs text-parchment-muted">@{user.username}</p>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => navigate(ROUTES.communities)}>
              <UserCircle className="h-4 w-4" />
              {t('nav.communities')}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => navigate(ROUTES.challenges)}>
              <Target className="h-4 w-4" />
              {t('nav.challenges')}
            </DropdownMenuItem>
            {user.isAdmin && (
              <DropdownMenuItem onSelect={() => navigate(ROUTES.adminChallenges)}>
                <Shield className="h-4 w-4" />
                {t('nav.admin')}
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
          </>
        )}
        <DropdownMenuItem onSelect={() => navigate(ROUTES.profile)}>
          <UserIcon className="h-4 w-4" />
          {t('nav.profile')}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => navigate(ROUTES.achievements)}>
          <Award className="h-4 w-4" />
          {t('nav.achievements')}
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={() => navigate(ROUTES.settings)}>
          <Settings className="h-4 w-4" />
          {t('nav.settings')}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          destructive
          onSelect={async () => {
            await logout();
            navigate(ROUTES.login, { replace: true });
          }}
        >
          <LogOut className="h-4 w-4" />
          {t('nav.signOut')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
